import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { saveManualCampaign } from '../api/use-campaigns';
import { MessageFieldWithEmoji } from './message-field-with-emoji';

interface Props {
  idPassbook: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  /** Operator multi-loja — POST /multi-message (só mensagem). */
  multiStore?: boolean;
  userId?: string;
}

/** Criação de campanha de e-mail/push: nome + mensagem (exibida no cartão). */
export function CampaignCreateDialog({
  idPassbook,
  open,
  onOpenChange,
  onCreated,
  multiStore = false,
  userId,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  const save = async () => {
    if (!description.trim()) {
      toast.error('Preencha a mensagem.');
      return;
    }
    if (!multiStore && !name.trim()) {
      toast.error('Preencha o nome e a mensagem.');
      return;
    }
    setBusy(true);
    try {
      await saveManualCampaign(
        idPassbook,
        { name, description },
        multiStore && userId ? { multiStore: true, userId } : undefined,
      );
      toast.success('Campanha criada.');
      onOpenChange(false);
      onCreated();
    } catch {
      toast.error('Falha ao criar campanha.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{multiStore ? 'Nova mensagem nas lojas' : 'Nova mensagem no cartão'}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          A mensagem aparece na tela de bloqueio e em “Últimas atualizações” no cartão dos clientes.
        </p>
        <div className="space-y-4">
          {!multiStore ? (
            <div className="space-y-2">
              <Label htmlFor="camp-name">Nome da campanha</Label>
              <Input
                id="camp-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex.: Lançamento Watermelon"
              />
            </div>
          ) : null}
          <MessageFieldWithEmoji
            id="camp-msg"
            label="Mensagem"
            rows={4}
            value={description}
            onChange={setDescription}
            placeholder="Digite a mensagem da campanha…"
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} loading={busy}>
            Criar campanha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
